import type { Component } from "vue";

export type RouteComponentModule = { default: Component };
export type RouteComponentLoader = () => Promise<RouteComponentModule>;

const rawRouteComponentLoaders = {
  promotion: () => import("@/features/promotion/PromotionPage.vue"),
  "app-layout": () => import("@/layouts/AppLayout.vue"),
  profile: () => import("@/pages/Profile.vue"),
  roles: () => import("@/pages/Roles.vue"),
  tracking: () => import("@/pages/Tracking.vue"),
  "mentor-matching": () => import("@/pages/MentorMatching.vue"),
  "mentor-availability": () => import("@/pages/MentorAvailability.vue"),
  "mentor-booking": () => import("@/pages/MentorBooking.vue"),
  "mentor-sessions": () => import("@/pages/MentorSessions.vue"),
  "support-tip-return": () => import("@/pages/SupportTipReturn.vue"),
  account: () => import("@/pages/Account.vue"),
  "manage-plan": () => import("@/pages/ManagePlan.vue"),
  "compare-plans": () => import("@/pages/ComparePlans.vue"),
  review: () => import("@/pages/Review.vue"),
  history: () => import("@/pages/History.vue"),
  inbox: () => import("@/pages/Inbox.vue"),
  "company-wishlist": () => import("@/pages/CompanyWishlist.vue"),
  "skilled-worker-list": () => import("@/pages/SkilledWorkerList.vue"),
  documents: () => import("@/pages/Documents.vue"),
  "document-editor": () => import("@/pages/DocumentEditor.vue"),
  "document-print": () => import("@/pages/DocumentPrint.vue"),
  "admin-overview": () => import("@/pages/admin/AdminOverview.vue"),
  "admin-users": () => import("@/pages/admin/AdminUsers.vue"),
  "admin-applications": () => import("@/pages/admin/AdminApplications.vue"),
  "admin-support": () => import("@/pages/admin/AdminSupport.vue"),
  "admin-commercial": () => import("@/pages/admin/AdminCommercial.vue"),
  "not-found": () => import("@/pages/NotFound.vue"),
} satisfies Record<string, RouteComponentLoader>;

export type AppRouteComponentName = keyof typeof rawRouteComponentLoaders;

export function createRoutePreloader<Name extends string, Module>(
  loaders: Readonly<Record<Name, () => Promise<Module>>>,
) {
  const loads = new Map<Name, Promise<Module>>();

  function preload(name: Name): Promise<Module> {
    const existing = loads.get(name);
    if (existing) return existing;

    const loading = Promise.resolve().then(loaders[name]);
    loads.set(name, loading);
    void loading.catch(() => {
      if (loads.get(name) === loading) loads.delete(name);
    });
    return loading;
  }

  function request(name: Name): void {
    void preload(name).catch(() => undefined);
  }

  return { preload, request };
}

const appRoutePreloader = createRoutePreloader(rawRouteComponentLoaders);

export function preloadRouteComponent(name: AppRouteComponentName): Promise<RouteComponentModule> {
  return appRoutePreloader.preload(name);
}

export function requestRoutePreload(name: AppRouteComponentName): void {
  appRoutePreloader.request(name);
}

export const routeComponentLoaders = Object.fromEntries(
  Object.keys(rawRouteComponentLoaders).map((name) => [
    name,
    () => preloadRouteComponent(name as AppRouteComponentName),
  ]),
) as Record<AppRouteComponentName, RouteComponentLoader>;

export interface RoutePreloadScheduler {
  cancel(handle: number): void;
  schedule(callback: () => void): number;
}

export interface BrowserRoutePreloadScope {
  cancelIdleCallback?: (handle: number) => void;
  clearTimeout(handle: number): void;
  requestIdleCallback?: (callback: () => void) => number;
  setTimeout(callback: () => void, delay: number): number;
}

export interface ScheduleRoutePreloadsOptions {
  requestPreload?: (name: AppRouteComponentName) => void;
  scheduler?: RoutePreloadScheduler;
}

export const SECONDARY_ROUTE_NAMES = [
  "company-wishlist",
  "skilled-worker-list",
  "documents",
  "review",
  "inbox",
  "history",
  "account",
] as const satisfies readonly AppRouteComponentName[];

export function createBrowserRoutePreloadScheduler(
  scope: BrowserRoutePreloadScope = window,
): RoutePreloadScheduler {
  if (scope.requestIdleCallback && scope.cancelIdleCallback) {
    return {
      cancel: (handle) => scope.cancelIdleCallback?.(handle),
      schedule: (callback) => scope.requestIdleCallback?.(callback) ?? 0,
    };
  }
  return {
    cancel: (handle) => scope.clearTimeout(handle),
    schedule: (callback) => scope.setTimeout(callback, 32),
  };
}

export function scheduleRoutePreloads(
  names: readonly AppRouteComponentName[] = SECONDARY_ROUTE_NAMES,
  options: ScheduleRoutePreloadsOptions = {},
): () => void {
  const scheduler = options.scheduler ?? createBrowserRoutePreloadScheduler();
  const requestPreload = options.requestPreload ?? requestRoutePreload;
  let currentHandle: number | null = null;
  let currentIndex = 0;
  let stopped = false;

  function scheduleNext(): void {
    currentHandle = scheduler.schedule(runNext);
  }

  function runNext(): void {
    currentHandle = null;
    if (stopped) return;
    const name = names[currentIndex];
    currentIndex += 1;
    if (!name) return;
    requestPreload(name);
    if (currentIndex < names.length) scheduleNext();
  }

  if (names.length > 0) scheduleNext();

  return () => {
    stopped = true;
    if (currentHandle !== null) scheduler.cancel(currentHandle);
    currentHandle = null;
  };
}
